import { useState } from "react";
import DriverCard from "./DriverCard";
import useCRUD from "../../hooks/useCRUD";

const RidesList = ({
  eventId,
  eventParticipations,
  refreshData,
  isEventExpired,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const ridesByDriver = eventParticipations.reduce((acc, participant) => {
    if (participant.isDriver || !participant.assignedDriverId) return acc;
    const driverId = participant.assignedDriverId.id;
    acc[driverId] = acc[driverId] || [];
    acc[driverId].push(participant);
    return acc;
  }, {});

  const { updateItem: updateEventParticipation } = useCRUD(
    "/api/event-participations",
  );

  const { addItem: sendNotification } = useCRUD(
    "/api/event-participations/send-notification-to-driver",
  );

  const { addItem: sendNotificationToAllDrivers } = useCRUD(
    "/api/event-participations/send-notification-to-all-drivers",
  );

  // Filtering drivers who are available and have been assigned at least one participant
  const driversWithParticipants = eventParticipations.filter(
    (p) =>
      p.isDriver &&
      p.availability !== "No" &&
      ridesByDriver[p.participantId]?.length > 0,
  );

  const allParticipants = eventParticipations.filter((p) => !p.isDriver);

  const assignedLoadByDriver = allParticipants.reduce((acc, participant) => {
    const driverId = participant.assignedDriverId?.id;
    if (!driverId) return acc;
    acc[driverId] = (acc[driverId] || 0) + (participant.noOfPpl || 1);
    return acc;
  }, {});

  const handleSendNotification = async (driverId) => {
    setIsLoading(true);
    console.log("Sending notification to", driverId);

    try {
      const body = {
        id: driverId,
      };
      await sendNotification(body);
      console.log("Notification sent successfully");
    } catch (error) {
      console.error("Error sending notification:", error);
    } finally {
      await refreshData(); // Refresh the list to reflect changes

      setIsLoading(false);
    }

    // Implement your notification logic here
    setIsLoading(false);
  };

  const handleSendNotificationToAllDrivers = async () => {
    setIsLoading(true);

    try {
      const body = {
        id: eventId,
      };
      await sendNotificationToAllDrivers(body);
      console.log("Notification sent successfully");
    } catch (error) {
      console.error("Error sending notification:", error);
    } finally {
      await refreshData(); // Refresh the list to reflect changes

      setIsLoading(false);
    }

    // Implement your notification logic here
    setIsLoading(false);
  };

  const onManagePickups = async (driverId, participantId, action) => {
    setIsLoading(true);
    try {
      // Assuming you have a function to update the event participation list
      // This could involve calling an API to update the backend
      if (action === "add") {
        // Add participant to driver's list

        await updateEventParticipation(participantId.id, {
          assignedDriverId: driverId,
        });

        console.log(
          `Adding participant ${participantId} to driver ${driverId}`,
        );
        // Your logic to update the assignment
      } else if (action === "remove") {
        // Remove participant from driver's list

        await updateEventParticipation(participantId.id, {
          assignedDriverId: null,
        });

        console.log(
          `Removing participant ${participantId} from driver ${driverId}`,
        );
        // Your logic to update the assignment
      }
    } catch (error) {
      console.error("Error managing pickups:", error);
    } finally {
      await refreshData(); // Refresh the list to reflect changes

      setIsLoading(false);
    }
  };

  return (
    <div className="card w-full max-w-full overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
      <div className="p-3 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#004a7e]">
              Ride assignment
            </p>
            <h3 className="text-xl font-black text-gray-900">Event Rides</h3>
          </div>
          <div
            className="tooltip"
            data-tip="Sends a notification to all drivers with a link to their pickup list. Once Driver confirms, a notification will go out to students"
          >
            {driversWithParticipants?.length > 0 && (
              <button
                className="rounded-lg bg-[#004a7e] px-3 py-2 text-sm font-bold text-white hover:bg-[#003a64] flex items-center justify-center gap-2"
                onClick={() => handleSendNotificationToAllDrivers()}
                disabled={isEventExpired}
              >
                <i className="fas fa-bell"></i>
                Notify Drivers
              </button>
            )}
          </div>
        </div>
        {driversWithParticipants?.length < 1 && (
          <p className="rounded-lg bg-gray-50 p-5 text-center text-gray-600">
            No rides assigned. Click Assign Driver to assign drivers to
            participants.
          </p>
        )}

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {driversWithParticipants &&
            driversWithParticipants.map((driver) => (
              <DriverCard
                key={driver.id}
                driver={driver}
                rides={ridesByDriver[driver.participantId] || []}
                onSendNotification={handleSendNotification}
                isLoading={isLoading}
                allParticipants={allParticipants}
                onManagePickups={onManagePickups}
                assignedLoad={assignedLoadByDriver[driver.participantId] || 0}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default RidesList;
